/**
 * Produktverwaltung V2 - Erweitertes Artikel-System für Cash-X
 * 
 * Features:
 * - Kategorien mit Icons und Farben
 * - Produkte mit Varianten (Größen)
 * - Modifier-Gruppen (Pflicht/Optional/Entfernen)
 * - Combo/Menü-Optionen
 * - Allergene (14 EU-Allergene)
 * - "86" Ausverkauft-Funktion
 * - Drag & Drop Sortierung
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Search, Filter, MoreVertical, Edit, Trash2,
  ChevronRight, ChevronDown, GripVertical, X, Check,
  AlertTriangle, Package, Tag, Settings, Save,
  Copy, Eye, EyeOff, Printer, ArrowLeft, RefreshCw,
  AlertCircle, Layers, List, Grid, ToggleLeft, ToggleRight
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Kategorie-Icons
const CATEGORY_ICONS = [
  { icon: "🍔", name: "Burger" },
  { icon: "🍕", name: "Pizza" },
  { icon: "🌮", name: "Tacos" },
  { icon: "🍟", name: "Pommes" },
  { icon: "🥗", name: "Salat" },
  { icon: "🍝", name: "Pasta" },
  { icon: "🍣", name: "Sushi" },
  { icon: "🥪", name: "Sandwich" },
  { icon: "🍜", name: "Suppe" },
  { icon: "🍗", name: "Hähnchen" },
  { icon: "🥩", name: "Fleisch" },
  { icon: "🐟", name: "Fisch" },
  { icon: "🥤", name: "Getränke" },
  { icon: "🍺", name: "Bier" },
  { icon: "🍷", name: "Wein" },
  { icon: "☕", name: "Kaffee" },
  { icon: "🍰", name: "Dessert" },
  { icon: "🍦", name: "Eis" },
  { icon: "🥐", name: "Frühstück" },
  { icon: "🍱", name: "Menü" },
  { icon: "⭐", name: "Spezial" },
  { icon: "🔥", name: "Beliebt" },
  { icon: "🆕", name: "Neu" },
  { icon: "🎁", name: "Angebote" }
];

// 14 EU-Allergene
const ALLERGENS = [
  { id: "gluten", name: "Glutenhaltiges Getreide", icon: "🌾" },
  { id: "crustaceans", name: "Krebstiere", icon: "🦐" },
  { id: "eggs", name: "Eier", icon: "🥚" },
  { id: "fish", name: "Fisch", icon: "🐟" },
  { id: "peanuts", name: "Erdnüsse", icon: "🥜" },
  { id: "soybeans", name: "Soja", icon: "🫘" },
  { id: "milk", name: "Milch/Laktose", icon: "🥛" },
  { id: "nuts", name: "Schalenfrüchte", icon: "🌰" },
  { id: "celery", name: "Sellerie", icon: "🥬" },
  { id: "mustard", name: "Senf", icon: "🟡" },
  { id: "sesame", name: "Sesam", icon: "⚪" },
  { id: "sulphites", name: "Schwefeldioxid/Sulfite", icon: "🍷" },
  { id: "lupin", name: "Lupinen", icon: "🌸" },
  { id: "molluscs", name: "Weichtiere", icon: "🦪" }
];

// Farben für Kategorien
const CATEGORY_COLORS = [
  "#ef4444", "#f97316", "#f59e0b", "#eab308", "#84cc16",
  "#22c55e", "#10b981", "#14b8a6", "#06b6d4", "#0ea5e9",
  "#3b82f6", "#6366f1", "#8b5cf6", "#a855f7", "#d946ef",
  "#ec4899", "#f43f5e"
];

export default function ProductsV2() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [sessionToken, setSessionToken] = useState(null);
  
  // Data
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [modifierTemplates, setModifierTemplates] = useState([]);
  const [comboTemplates, setComboTemplates] = useState([]);
  
  // UI State
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'grid'
  const [showInactive, setShowInactive] = useState(false);
  
  // Modals
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [editingProduct, setEditingProduct] = useState(null);
  
  // Category Form
  const [categoryForm, setCategoryForm] = useState({
    name: '',
    icon: '🍽️',
    color: '#3b82f6',
    sort_order: 0,
    is_active: true
  });
  
  // Product Form
  const [productForm, setProductForm] = useState({
    name: '',
    category_id: '',
    description: '',
    base_price: 0,
    tax_rate: 19,
    product_type: 'food',
    variants: [],
    modifier_groups: [],
    combo_options: [],
    allergens: [],
    printer_id: null,
    sku: '',
    calories: null,
    is_active: true,
    is_available: true
  });

  // Token laden
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      navigate('/login');
      return;
    }
    setSessionToken(token);
  }, [navigate]);

  // Daten laden
  const loadData = useCallback(async () => {
    if (!sessionToken) return;
    
    try {
      setLoading(true);
      
      const headers = { 'Authorization': `Bearer ${sessionToken}` };
      
      const [categoriesRes, productsRes, templatesRes, comboRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/products/v2/categories`, { headers }),
        fetch(`${BACKEND_URL}/api/products/v2/`, { headers }),
        fetch(`${BACKEND_URL}/api/products/v2/templates/modifier-groups`, { headers }),
        fetch(`${BACKEND_URL}/api/products/v2/templates/combos`, { headers })
      ]);
      
      if (categoriesRes.ok) {
        const data = await categoriesRes.json();
        setCategories(data);
      }
      
      if (productsRes.ok) {
        const data = await productsRes.json();
        setProducts(data);
      }
      
      if (templatesRes.ok) {
        const data = await templatesRes.json();
        setModifierTemplates(data.templates || []);
      }
      
      if (comboRes.ok) {
        const data = await comboRes.json();
        setComboTemplates(data.templates || []);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [sessionToken]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // API Calls
  const saveCategory = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json'
      };
      
      const method = editingCategory ? 'PUT' : 'POST';
      const url = editingCategory 
        ? `${BACKEND_URL}/api/products/v2/categories/${editingCategory.id}`
        : `${BACKEND_URL}/api/products/v2/categories`;
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(categoryForm)
      });
      
      if (res.ok) {
        toast.success(editingCategory ? 'Kategorie aktualisiert' : 'Kategorie erstellt');
        setShowCategoryModal(false);
        resetCategoryForm();
        loadData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!window.confirm('Kategorie wirklich löschen?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/v2/categories/${categoryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (res.ok) {
        toast.success('Kategorie gelöscht');
        loadData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  const saveProduct = async () => {
    try {
      const headers = {
        'Authorization': `Bearer ${sessionToken}`,
        'Content-Type': 'application/json'
      };
      
      const method = editingProduct ? 'PUT' : 'POST';
      const url = editingProduct 
        ? `${BACKEND_URL}/api/products/v2/${editingProduct.id}`
        : `${BACKEND_URL}/api/products/v2/`;
      
      const res = await fetch(url, {
        method,
        headers,
        body: JSON.stringify(productForm)
      });
      
      if (res.ok) {
        toast.success(editingProduct ? 'Produkt aktualisiert' : 'Produkt erstellt');
        setShowProductModal(false);
        resetProductForm();
        loadData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Speichern');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  const deleteProduct = async (productId) => {
    if (!window.confirm('Produkt wirklich löschen?')) return;
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/v2/${productId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (res.ok) {
        toast.success('Produkt gelöscht');
        loadData();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Fehler beim Löschen');
      }
    } catch (error) {
      toast.error('Verbindungsfehler');
    }
  };

  const toggleProductAvailable = async (product) => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/products/v2/${product.id}/toggle-available`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${sessionToken}` }
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        loadData();
      }
    } catch (error) {
      toast.error('Fehler');
    }
  };

  // Form Helpers
  const resetCategoryForm = () => {
    setCategoryForm({
      name: '',
      icon: '🍽️',
      color: '#3b82f6',
      sort_order: 0,
      is_active: true
    });
    setEditingCategory(null);
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      category_id: selectedCategory?.id || '',
      description: '',
      base_price: 0,
      tax_rate: 19,
      product_type: 'food',
      variants: [],
      modifier_groups: [],
      combo_options: [],
      allergens: [],
      printer_id: null,
      sku: '',
      calories: null,
      is_active: true,
      is_available: true
    });
    setEditingProduct(null);
  };

  const openEditCategory = (category) => {
    setEditingCategory(category);
    setCategoryForm({
      name: category.name,
      icon: category.icon || '🍽️',
      color: category.color || '#3b82f6',
      sort_order: category.sort_order || 0,
      is_active: category.is_active
    });
    setShowCategoryModal(true);
  };

  const openEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      category_id: product.category_id,
      description: product.description || '',
      base_price: product.base_price,
      tax_rate: product.tax_rate,
      product_type: product.product_type || 'food',
      variants: product.variants || [],
      modifier_groups: product.modifier_groups || [],
      combo_options: product.combo_options || [],
      allergens: product.allergens || [],
      printer_id: product.printer_id,
      sku: product.sku || '',
      calories: product.calories,
      is_active: product.is_active,
      is_available: product.is_available
    });
    setShowProductModal(true);
  };

  // Variant Management
  const addVariant = () => {
    setProductForm(prev => ({
      ...prev,
      variants: [...prev.variants, {
        name: '',
        price: prev.base_price,
        sku: '',
        is_default: prev.variants.length === 0,
        sort_order: prev.variants.length
      }]
    }));
  };

  const updateVariant = (index, field, value) => {
    setProductForm(prev => {
      const newVariants = [...prev.variants];
      newVariants[index] = { ...newVariants[index], [field]: value };
      return { ...prev, variants: newVariants };
    });
  };

  const removeVariant = (index) => {
    setProductForm(prev => ({
      ...prev,
      variants: prev.variants.filter((_, i) => i !== index)
    }));
  };

  // Modifier Group Management
  const addModifierGroup = (template = null) => {
    const newGroup = template ? {
      ...template,
      options: template.options.map(o => ({ ...o }))
    } : {
      name: '',
      type: 'optional',
      min_selections: 0,
      max_selections: 99,
      options: [],
      sort_order: productForm.modifier_groups.length
    };
    
    setProductForm(prev => ({
      ...prev,
      modifier_groups: [...prev.modifier_groups, newGroup]
    }));
  };

  const updateModifierGroup = (index, field, value) => {
    setProductForm(prev => {
      const newGroups = [...prev.modifier_groups];
      newGroups[index] = { ...newGroups[index], [field]: value };
      return { ...prev, modifier_groups: newGroups };
    });
  };

  const removeModifierGroup = (index) => {
    setProductForm(prev => ({
      ...prev,
      modifier_groups: prev.modifier_groups.filter((_, i) => i !== index)
    }));
  };

  const addModifierOption = (groupIndex) => {
    setProductForm(prev => {
      const newGroups = [...prev.modifier_groups];
      newGroups[groupIndex].options = [
        ...newGroups[groupIndex].options,
        { name: '', price: 0, is_default: false, sort_order: newGroups[groupIndex].options.length }
      ];
      return { ...prev, modifier_groups: newGroups };
    });
  };

  const updateModifierOption = (groupIndex, optionIndex, field, value) => {
    setProductForm(prev => {
      const newGroups = [...prev.modifier_groups];
      newGroups[groupIndex].options[optionIndex] = {
        ...newGroups[groupIndex].options[optionIndex],
        [field]: value
      };
      return { ...prev, modifier_groups: newGroups };
    });
  };

  const removeModifierOption = (groupIndex, optionIndex) => {
    setProductForm(prev => {
      const newGroups = [...prev.modifier_groups];
      newGroups[groupIndex].options = newGroups[groupIndex].options.filter((_, i) => i !== optionIndex);
      return { ...prev, modifier_groups: newGroups };
    });
  };

  // Combo Option Management
  const addComboOption = (template = null) => {
    const newCombo = template ? { ...template } : {
      name: '',
      price: 0,
      description: '',
      included_items: [],
      sort_order: productForm.combo_options.length
    };
    
    setProductForm(prev => ({
      ...prev,
      combo_options: [...prev.combo_options, newCombo]
    }));
  };

  const updateComboOption = (index, field, value) => {
    setProductForm(prev => {
      const newCombos = [...prev.combo_options];
      newCombos[index] = { ...newCombos[index], [field]: value };
      return { ...prev, combo_options: newCombos };
    });
  };

  const removeComboOption = (index) => {
    setProductForm(prev => ({
      ...prev,
      combo_options: prev.combo_options.filter((_, i) => i !== index)
    }));
  };

  // Allergen Management
  const toggleAllergen = (allergenId) => {
    setProductForm(prev => {
      const newAllergens = prev.allergens.includes(allergenId)
        ? prev.allergens.filter(a => a !== allergenId)
        : [...prev.allergens, allergenId];
      return { ...prev, allergens: newAllergens };
    });
  };

  // Filter Products
  const filteredProducts = products.filter(p => {
    if (selectedCategory && p.category_id !== selectedCategory.id) return false;
    if (!showInactive && !p.is_active) return false;
    if (searchTerm && !p.name.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-center text-white">
          <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-500" />
          <p>Lade Produktdaten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate(-1)}
                className="text-gray-400 hover:text-white"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Zurück
              </Button>
              <div>
                <h1 className="text-xl font-bold flex items-center gap-2">
                  <Package className="w-5 h-5 text-blue-500" />
                  Produktverwaltung
                </h1>
                <p className="text-sm text-gray-400">
                  {categories.length} Kategorien • {products.length} Produkte
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  resetCategoryForm();
                  setShowCategoryModal(true);
                }}
                className="border-gray-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Kategorie
              </Button>
              <Button
                size="sm"
                onClick={() => {
                  resetProductForm();
                  setShowProductModal(true);
                }}
                className="bg-blue-600 hover:bg-blue-700"
              >
                <Plus className="w-4 h-4 mr-2" />
                Produkt
              </Button>
            </div>
          </div>
          
          {/* Search & Filter Bar */}
          <div className="mt-4 flex items-center gap-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Produkte suchen..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 bg-gray-700 border-gray-600"
              />
            </div>
            
            <div className="flex items-center gap-2">
              <Button
                variant={viewMode === 'list' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('list')}
              >
                <List className="w-4 h-4" />
              </Button>
              <Button
                variant={viewMode === 'grid' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setViewMode('grid')}
              >
                <Grid className="w-4 h-4" />
              </Button>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={showInactive}
                onCheckedChange={setShowInactive}
              />
              <span className="text-sm text-gray-400">Inaktive zeigen</span>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Categories Sidebar */}
          <div className="w-64 flex-shrink-0">
            <Card className="bg-gray-800 border-gray-700 sticky top-32">
              <CardHeader className="pb-2">
                <CardTitle className="text-white text-sm">Kategorien</CardTitle>
              </CardHeader>
              <CardContent className="p-2">
                <div className="space-y-1">
                  <button
                    onClick={() => setSelectedCategory(null)}
                    className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-left transition-colors ${
                      !selectedCategory ? 'bg-blue-600 text-white' : 'hover:bg-gray-700 text-gray-300'
                    }`}
                  >
                    <Layers className="w-4 h-4" />
                    <span>Alle Produkte</span>
                    <Badge variant="secondary" className="ml-auto">{products.length}</Badge>
                  </button>
                  
                  {categories.map(category => (
                    <div
                      key={category.id}
                      className={`group flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer transition-colors ${
                        selectedCategory?.id === category.id ? 'bg-blue-600 text-white' : 'hover:bg-gray-700 text-gray-300'
                      }`}
                    >
                      <button
                        onClick={() => setSelectedCategory(category)}
                        className="flex items-center gap-2 flex-1"
                      >
                        <span className="text-lg">{category.icon || '📁'}</span>
                        <span className="truncate">{category.name}</span>
                        <Badge variant="secondary" className="ml-auto">
                          {category.product_count || 0}
                        </Badge>
                      </button>
                      
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="opacity-0 group-hover:opacity-100 h-6 w-6 p-0"
                          >
                            <MoreVertical className="w-3 h-3" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>
                          <DropdownMenuItem onClick={() => openEditCategory(category)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Bearbeiten
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => deleteCategory(category.id)}
                            className="text-red-500"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Löschen
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Products Grid/List */}
          <div className="flex-1">
            {filteredProducts.length === 0 ? (
              <Card className="bg-gray-800 border-gray-700">
                <CardContent className="py-12 text-center">
                  <Package className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-white mb-2">
                    Keine Produkte gefunden
                  </h3>
                  <p className="text-gray-400 mb-4">
                    {searchTerm ? 'Versuchen Sie eine andere Suche' : 'Erstellen Sie Ihr erstes Produkt'}
                  </p>
                  <Button
                    onClick={() => {
                      resetProductForm();
                      setShowProductModal(true);
                    }}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Produkt erstellen
                  </Button>
                </CardContent>
              </Card>
            ) : viewMode === 'list' ? (
              <div className="space-y-2">
                {filteredProducts.map(product => {
                  const category = categories.find(c => c.id === product.category_id);
                  
                  return (
                    <Card 
                      key={product.id}
                      className={`bg-gray-800 border-gray-700 ${
                        !product.is_available ? 'opacity-60' : ''
                      } ${!product.is_active ? 'border-l-4 border-l-red-500' : ''}`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-center gap-4">
                          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-2xl">
                            {category?.icon || '🍽️'}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="font-medium text-white truncate">{product.name}</h3>
                              {!product.is_available && (
                                <Badge variant="destructive" className="text-xs">
                                  Ausverkauft
                                </Badge>
                              )}
                              {!product.is_active && (
                                <Badge variant="secondary" className="text-xs">
                                  Inaktiv
                                </Badge>
                              )}
                            </div>
                            <div className="flex items-center gap-3 text-sm text-gray-400">
                              <span>{category?.name || 'Keine Kategorie'}</span>
                              {product.variants?.length > 1 && (
                                <span>• {product.variants.length} Varianten</span>
                              )}
                              {product.modifier_groups?.length > 0 && (
                                <span>• {product.modifier_groups.length} Modifier</span>
                              )}
                              {product.allergens?.length > 0 && (
                                <span>• {product.allergens.length} Allergene</span>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-lg font-bold text-white">
                              {product.base_price?.toFixed(2)} €
                            </div>
                            <div className="text-xs text-gray-500">
                              {product.tax_rate}% MwSt
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleProductAvailable(product)}
                              className={product.is_available ? 'text-green-500' : 'text-red-500'}
                            >
                              {product.is_available ? (
                                <Eye className="w-4 h-4" />
                              ) : (
                                <EyeOff className="w-4 h-4" />
                              )}
                            </Button>
                            
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm">
                                  <MoreVertical className="w-4 h-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent>
                                <DropdownMenuItem onClick={() => openEditProduct(product)}>
                                  <Edit className="w-4 h-4 mr-2" />
                                  Bearbeiten
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => {/* duplicate */}}>
                                  <Copy className="w-4 h-4 mr-2" />
                                  Duplizieren
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={() => deleteProduct(product.id)}
                                  className="text-red-500"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Löschen
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredProducts.map(product => {
                  const category = categories.find(c => c.id === product.category_id);
                  
                  return (
                    <Card 
                      key={product.id}
                      className={`bg-gray-800 border-gray-700 ${
                        !product.is_available ? 'opacity-60' : ''
                      }`}
                    >
                      <CardContent className="p-4">
                        <div className="flex items-start justify-between mb-3">
                          <div className="w-12 h-12 rounded-lg bg-gray-700 flex items-center justify-center text-2xl">
                            {category?.icon || '🍽️'}
                          </div>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm">
                                <MoreVertical className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent>
                              <DropdownMenuItem onClick={() => openEditProduct(product)}>
                                <Edit className="w-4 h-4 mr-2" />
                                Bearbeiten
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => toggleProductAvailable(product)}>
                                {product.is_available ? (
                                  <>
                                    <EyeOff className="w-4 h-4 mr-2" />
                                    Als ausverkauft markieren
                                  </>
                                ) : (
                                  <>
                                    <Eye className="w-4 h-4 mr-2" />
                                    Wieder verfügbar
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem 
                                onClick={() => deleteProduct(product.id)}
                                className="text-red-500"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Löschen
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        
                        <h3 className="font-medium text-white mb-1">{product.name}</h3>
                        <p className="text-sm text-gray-400 mb-3">{category?.name}</p>
                        
                        <div className="flex items-center justify-between">
                          <span className="text-lg font-bold text-white">
                            {product.base_price?.toFixed(2)} €
                          </span>
                          <div className="flex gap-1">
                            {!product.is_available && (
                              <Badge variant="destructive" className="text-xs">86</Badge>
                            )}
                            {product.combo_options?.length > 0 && (
                              <Badge variant="secondary" className="text-xs">Menü</Badge>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Modal */}
      <Dialog open={showCategoryModal} onOpenChange={setShowCategoryModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingCategory ? 'Kategorie bearbeiten' : 'Neue Kategorie'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Name</Label>
              <Input
                value={categoryForm.name}
                onChange={(e) => setCategoryForm(prev => ({ ...prev, name: e.target.value }))}
                placeholder="z.B. Burger"
                className="bg-gray-700 border-gray-600"
              />
            </div>
            
            <div>
              <Label>Icon</Label>
              <div className="grid grid-cols-8 gap-2 mt-2">
                {CATEGORY_ICONS.map(({ icon, name }) => (
                  <button
                    key={icon}
                    onClick={() => setCategoryForm(prev => ({ ...prev, icon }))}
                    className={`w-10 h-10 rounded-lg flex items-center justify-center text-xl transition-colors ${
                      categoryForm.icon === icon 
                        ? 'bg-blue-600 ring-2 ring-blue-400' 
                        : 'bg-gray-700 hover:bg-gray-600'
                    }`}
                    title={name}
                  >
                    {icon}
                  </button>
                ))}
              </div>
            </div>
            
            <div>
              <Label>Farbe</Label>
              <div className="grid grid-cols-9 gap-2 mt-2">
                {CATEGORY_COLORS.map(color => (
                  <button
                    key={color}
                    onClick={() => setCategoryForm(prev => ({ ...prev, color }))}
                    className={`w-8 h-8 rounded-full transition-transform ${
                      categoryForm.color === color ? 'ring-2 ring-white scale-110' : ''
                    }`}
                    style={{ backgroundColor: color }}
                  />
                ))}
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <Label>Aktiv</Label>
              <Switch
                checked={categoryForm.is_active}
                onCheckedChange={(checked) => setCategoryForm(prev => ({ ...prev, is_active: checked }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCategoryModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={saveCategory} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              Speichern
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Product Modal */}
      <Dialog open={showProductModal} onOpenChange={setShowProductModal}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingProduct ? 'Produkt bearbeiten' : 'Neues Produkt'}
            </DialogTitle>
            <DialogDescription>
              Konfigurieren Sie alle Details für dieses Produkt
            </DialogDescription>
          </DialogHeader>
          
          <Tabs defaultValue="basic" className="w-full">
            <TabsList className="bg-gray-700 mb-4">
              <TabsTrigger value="basic">Grunddaten</TabsTrigger>
              <TabsTrigger value="variants">Varianten</TabsTrigger>
              <TabsTrigger value="modifiers">Modifier</TabsTrigger>
              <TabsTrigger value="combos">Menü-Optionen</TabsTrigger>
              <TabsTrigger value="allergens">Allergene</TabsTrigger>
            </TabsList>

            {/* Basic Tab */}
            <TabsContent value="basic" className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Produktname *</Label>
                  <Input
                    value={productForm.name}
                    onChange={(e) => setProductForm(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="z.B. Cheeseburger"
                    className="bg-gray-700 border-gray-600"
                  />
                </div>
                
                <div>
                  <Label>Kategorie *</Label>
                  <Select
                    value={productForm.category_id}
                    onValueChange={(value) => setProductForm(prev => ({ ...prev, category_id: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600">
                      <SelectValue placeholder="Kategorie wählen" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>
                          {cat.icon} {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div>
                <Label>Beschreibung</Label>
                <Textarea
                  value={productForm.description}
                  onChange={(e) => setProductForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Optionale Beschreibung des Produkts..."
                  className="bg-gray-700 border-gray-600"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Basispreis (€) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={productForm.base_price}
                    onChange={(e) => setProductForm(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                    className="bg-gray-700 border-gray-600"
                  />
                </div>
                
                <div>
                  <Label>MwSt-Satz</Label>
                  <Select
                    value={productForm.tax_rate.toString()}
                    onValueChange={(value) => setProductForm(prev => ({ ...prev, tax_rate: parseInt(value) }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="7">7% (Speisen)</SelectItem>
                      <SelectItem value="19">19% (Getränke)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label>Produkttyp</Label>
                  <Select
                    value={productForm.product_type}
                    onValueChange={(value) => setProductForm(prev => ({ ...prev, product_type: value }))}
                  >
                    <SelectTrigger className="bg-gray-700 border-gray-600">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="food">🍽️ Speise</SelectItem>
                      <SelectItem value="drink">🥤 Getränk</SelectItem>
                      <SelectItem value="combo">🍱 Menü</SelectItem>
                      <SelectItem value="extra">➕ Extra</SelectItem>
                      <SelectItem value="other">📦 Sonstiges</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>SKU / Artikelnummer</Label>
                  <Input
                    value={productForm.sku}
                    onChange={(e) => setProductForm(prev => ({ ...prev, sku: e.target.value }))}
                    placeholder="z.B. BUR-001"
                    className="bg-gray-700 border-gray-600"
                  />
                </div>
                
                <div>
                  <Label>Kalorien (optional)</Label>
                  <Input
                    type="number"
                    value={productForm.calories || ''}
                    onChange={(e) => setProductForm(prev => ({ ...prev, calories: e.target.value ? parseInt(e.target.value) : null }))}
                    placeholder="z.B. 650"
                    className="bg-gray-700 border-gray-600"
                  />
                </div>
              </div>
              
              <div className="flex items-center gap-6 pt-4 border-t border-gray-700">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={productForm.is_active}
                    onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, is_active: checked }))}
                  />
                  <Label>Aktiv</Label>
                </div>
                
                <div className="flex items-center gap-2">
                  <Switch
                    checked={productForm.is_available}
                    onCheckedChange={(checked) => setProductForm(prev => ({ ...prev, is_available: checked }))}
                  />
                  <Label>Verfügbar (nicht "86")</Label>
                </div>
              </div>
            </TabsContent>

            {/* Variants Tab */}
            <TabsContent value="variants" className="space-y-4">
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <AlertCircle className="w-4 h-4 text-blue-500" />
                <AlertDescription className="text-gray-300">
                  Varianten ermöglichen verschiedene Größen oder Ausführungen mit eigenen Preisen.
                  Ohne Varianten wird der Basispreis verwendet.
                </AlertDescription>
              </Alert>
              
              {productForm.variants.map((variant, index) => (
                <Card key={index} className="bg-gray-700 border-gray-600">
                  <CardContent className="p-4">
                    <div className="flex items-center gap-4">
                      <div className="flex-1">
                        <Label className="text-xs text-gray-400">Name</Label>
                        <Input
                          value={variant.name}
                          onChange={(e) => updateVariant(index, 'name', e.target.value)}
                          placeholder="z.B. Klein, Normal, Groß"
                          className="bg-gray-600 border-gray-500"
                        />
                      </div>
                      <div className="w-32">
                        <Label className="text-xs text-gray-400">Preis (€)</Label>
                        <Input
                          type="number"
                          step="0.01"
                          value={variant.price}
                          onChange={(e) => updateVariant(index, 'price', parseFloat(e.target.value) || 0)}
                          className="bg-gray-600 border-gray-500"
                        />
                      </div>
                      <div className="w-24">
                        <Label className="text-xs text-gray-400">Standard</Label>
                        <div className="pt-2">
                          <Switch
                            checked={variant.is_default}
                            onCheckedChange={(checked) => {
                              // Reset all others
                              setProductForm(prev => ({
                                ...prev,
                                variants: prev.variants.map((v, i) => ({
                                  ...v,
                                  is_default: i === index ? checked : false
                                }))
                              }));
                            }}
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeVariant(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              <Button
                variant="outline"
                onClick={addVariant}
                className="w-full border-dashed border-gray-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Variante hinzufügen
              </Button>
            </TabsContent>

            {/* Modifiers Tab */}
            <TabsContent value="modifiers" className="space-y-4">
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <AlertCircle className="w-4 h-4 text-blue-500" />
                <AlertDescription className="text-gray-300">
                  Modifier ermöglichen Anpassungen wie Extras, Beilagen oder "OHNE"-Optionen.
                </AlertDescription>
              </Alert>
              
              {/* Templates */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-sm text-gray-400">Vorlagen:</span>
                {modifierTemplates.map((template, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => addModifierGroup(template)}
                    className="border-gray-600"
                  >
                    + {template.name}
                  </Button>
                ))}
              </div>
              
              <Accordion type="multiple" className="space-y-2">
                {productForm.modifier_groups.map((group, groupIndex) => (
                  <AccordionItem 
                    key={groupIndex} 
                    value={`group-${groupIndex}`}
                    className="bg-gray-700 border border-gray-600 rounded-lg overflow-hidden"
                  >
                    <AccordionTrigger className="px-4 py-3 hover:no-underline">
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{group.name || 'Neue Gruppe'}</span>
                        <Badge variant={
                          group.type === 'required' ? 'default' :
                          group.type === 'remove' ? 'destructive' : 'secondary'
                        }>
                          {group.type === 'required' ? 'Pflicht' :
                           group.type === 'remove' ? 'Entfernen' : 'Optional'}
                        </Badge>
                        <span className="text-sm text-gray-400">
                          ({group.options.length} Optionen)
                        </span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="px-4 pb-4">
                      <div className="space-y-4">
                        <div className="grid grid-cols-4 gap-4">
                          <div className="col-span-2">
                            <Label className="text-xs">Gruppenname</Label>
                            <Input
                              value={group.name}
                              onChange={(e) => updateModifierGroup(groupIndex, 'name', e.target.value)}
                              placeholder="z.B. Extras"
                              className="bg-gray-600 border-gray-500"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Typ</Label>
                            <Select
                              value={group.type}
                              onValueChange={(value) => updateModifierGroup(groupIndex, 'type', value)}
                            >
                              <SelectTrigger className="bg-gray-600 border-gray-500">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="required">Pflicht</SelectItem>
                                <SelectItem value="optional">Optional</SelectItem>
                                <SelectItem value="remove">Entfernen (OHNE)</SelectItem>
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="flex items-end">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeModifierGroup(groupIndex)}
                              className="text-red-400"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Gruppe löschen
                            </Button>
                          </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs">Min. Auswahl</Label>
                            <Input
                              type="number"
                              min="0"
                              value={group.min_selections}
                              onChange={(e) => updateModifierGroup(groupIndex, 'min_selections', parseInt(e.target.value) || 0)}
                              className="bg-gray-600 border-gray-500"
                            />
                          </div>
                          <div>
                            <Label className="text-xs">Max. Auswahl</Label>
                            <Input
                              type="number"
                              min="1"
                              value={group.max_selections}
                              onChange={(e) => updateModifierGroup(groupIndex, 'max_selections', parseInt(e.target.value) || 1)}
                              className="bg-gray-600 border-gray-500"
                            />
                          </div>
                        </div>
                        
                        <div className="border-t border-gray-600 pt-4">
                          <Label className="text-xs mb-2 block">Optionen</Label>
                          <div className="space-y-2">
                            {group.options.map((option, optionIndex) => (
                              <div key={optionIndex} className="flex items-center gap-2 bg-gray-600 p-2 rounded">
                                <Input
                                  value={option.name}
                                  onChange={(e) => updateModifierOption(groupIndex, optionIndex, 'name', e.target.value)}
                                  placeholder="Name"
                                  className="bg-gray-500 border-gray-400 flex-1"
                                />
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={option.price}
                                  onChange={(e) => updateModifierOption(groupIndex, optionIndex, 'price', parseFloat(e.target.value) || 0)}
                                  placeholder="Preis"
                                  className="bg-gray-500 border-gray-400 w-24"
                                />
                                <span className="text-gray-400 text-sm">€</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => removeModifierOption(groupIndex, optionIndex)}
                                  className="text-red-400 hover:text-red-300 p-1"
                                >
                                  <X className="w-4 h-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => addModifierOption(groupIndex)}
                            className="mt-2 text-blue-400"
                          >
                            <Plus className="w-4 h-4 mr-1" />
                            Option hinzufügen
                          </Button>
                        </div>
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
              
              <Button
                variant="outline"
                onClick={() => addModifierGroup()}
                className="w-full border-dashed border-gray-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Modifier-Gruppe hinzufügen
              </Button>
            </TabsContent>

            {/* Combos Tab */}
            <TabsContent value="combos" className="space-y-4">
              <Alert className="bg-blue-500/10 border-blue-500/30">
                <AlertCircle className="w-4 h-4 text-blue-500" />
                <AlertDescription className="text-gray-300">
                  Menü-Optionen ermöglichen Upgrades wie "Als Menü" mit Beilage und Getränk.
                </AlertDescription>
              </Alert>
              
              {/* Templates */}
              <div className="flex flex-wrap gap-2 mb-4">
                <span className="text-sm text-gray-400">Vorlagen:</span>
                {comboTemplates.map((template, index) => (
                  <Button
                    key={index}
                    variant="outline"
                    size="sm"
                    onClick={() => addComboOption(template)}
                    className="border-gray-600"
                  >
                    + {template.name}
                  </Button>
                ))}
              </div>
              
              {productForm.combo_options.map((combo, index) => (
                <Card key={index} className="bg-gray-700 border-gray-600">
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div className="flex-1 space-y-3">
                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <Label className="text-xs text-gray-400">Name</Label>
                            <Input
                              value={combo.name}
                              onChange={(e) => updateComboOption(index, 'name', e.target.value)}
                              placeholder="z.B. Als Menü"
                              className="bg-gray-600 border-gray-500"
                            />
                          </div>
                          <div>
                            <Label className="text-xs text-gray-400">Aufpreis (€)</Label>
                            <Input
                              type="number"
                              step="0.01"
                              value={combo.price}
                              onChange={(e) => updateComboOption(index, 'price', parseFloat(e.target.value) || 0)}
                              className="bg-gray-600 border-gray-500"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs text-gray-400">Beschreibung</Label>
                          <Input
                            value={combo.description}
                            onChange={(e) => updateComboOption(index, 'description', e.target.value)}
                            placeholder="z.B. Inkl. Pommes + Getränk (0,4l)"
                            className="bg-gray-600 border-gray-500"
                          />
                        </div>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeComboOption(index)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
              
              <Button
                variant="outline"
                onClick={() => addComboOption()}
                className="w-full border-dashed border-gray-600"
              >
                <Plus className="w-4 h-4 mr-2" />
                Menü-Option hinzufügen
              </Button>
            </TabsContent>

            {/* Allergens Tab */}
            <TabsContent value="allergens" className="space-y-4">
              <Alert className="bg-yellow-500/10 border-yellow-500/30">
                <AlertTriangle className="w-4 h-4 text-yellow-500" />
                <AlertTitle className="text-yellow-500">EU-Allergen-Verordnung</AlertTitle>
                <AlertDescription className="text-gray-300">
                  Gastronomie-Betriebe sind verpflichtet, die 14 Hauptallergene zu kennzeichnen.
                </AlertDescription>
              </Alert>
              
              <div className="grid grid-cols-2 gap-3">
                {ALLERGENS.map(allergen => (
                  <div
                    key={allergen.id}
                    onClick={() => toggleAllergen(allergen.id)}
                    className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                      productForm.allergens.includes(allergen.id)
                        ? 'bg-red-500/20 border border-red-500/50'
                        : 'bg-gray-700 hover:bg-gray-600 border border-transparent'
                    }`}
                  >
                    <Checkbox
                      checked={productForm.allergens.includes(allergen.id)}
                      className="pointer-events-none"
                    />
                    <span className="text-xl">{allergen.icon}</span>
                    <span className="text-sm">{allergen.name}</span>
                  </div>
                ))}
              </div>
              
              {productForm.allergens.length > 0 && (
                <div className="p-4 bg-gray-700 rounded-lg">
                  <p className="text-sm text-gray-400 mb-2">Ausgewählte Allergene:</p>
                  <div className="flex flex-wrap gap-2">
                    {productForm.allergens.map(id => {
                      const allergen = ALLERGENS.find(a => a.id === id);
                      return (
                        <Badge key={id} variant="secondary">
                          {allergen?.icon} {allergen?.name}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
          
          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setShowProductModal(false)}>
              Abbrechen
            </Button>
            <Button onClick={saveProduct} className="bg-blue-600 hover:bg-blue-700">
              <Save className="w-4 h-4 mr-2" />
              {editingProduct ? 'Aktualisieren' : 'Erstellen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
